<template>
	<div
		v-if="rewardGridItems && rewardGridItems.length > 0"
		class="rewards-grid"
		:class="`rewards-grid--${rewardGridItems.length > 1 ? 'multiple' : 'single'} rewards-grid--count-${count} rewards-grid--${context}`"
	>
		<div
			v-for="(item, idx) in rewardGridItems"
			:key="idx"
			class="rewards-grid__item"
		>
			<Tooltip
				v-if="item"
				:heading="translate(`${item.frame_name}.name`)"
				:disabled="hasTileAggregationDetails"
				customWidth="18rem"
				:placement="Placements.TOP"
			>
				<template #element>
					<div class="rewards-grid__value">
						<div
							class="rewards-grid__value-item"
							:class="{
								'rewards-grid__value-item--horizontal':
									rewardGridItems.length <= 2,
								'rewards-grid__value-item--vertical':
									rewardGridItems.length > 2,
							}"
							:style="{
								gap: `${(dynamicSize?.gap ?? 16) / 16}rem`,
							}"
						>
							<div
								class="rewards-grid__icon"
								:class="`icon-${getTileIconName(item.frame_name)}-${dynamicSize.icon}`"
							/>

							<div class="rewards-grid__value-aside">
								<div
									class="rewards-grid__label"
									:style="{
										fontSize: `${(dynamicSize?.text ?? 20) / 16}rem`,
									}"
								>
									{{ `${item.qty}x` }}
								</div>
								<div
									v-if="
										context === RewardsGridContext.POPUP &&
										!hasTileAggregationDetails &&
										rewardGridItems.length === 1
									"
									class="reward-item-aside-label-wrapper"
								>
									<ShrinkText
										:text="
											translate(`${item.frame_name}.name`)
										"
										:fontSize="14"
										:forcedFontSize="14"
										:maxLineLimit="2"
										containerClassList="reward-item-aside-label"
										isBold
										isUppercase
										:textAlign="TextAlign.LEFT"
									/>
								</div>
							</div>
						</div>

						<div
							v-if="hasTileAggregationDetails"
							class="rewards-grid__content-wrapper"
						>
							<BidirectionalTextLabel
								:text="translate(`${item.frame_name}.name`)"
								:theme="LabelThemes.RED"
								class="rewards-label"
							/>
							<div class="rewards-grid__content">
								<p
									v-if="item.frame_name"
									v-html="
										switchParagraphFormat(
											item?.reward_entity?.description ??
												'',
										)
									"
								/>
								<TileAggregationDetails
									:entity="item?.reward_entity as TileEntity"
									:isPromotion="true"
								/>
							</div>
							<RectangleBoxContainer />
						</div>
					</div>
					<div
						v-if="
							context === RewardsGridContext.POPUP &&
							!hasTileAggregationDetails &&
							rewardGridItems.length > 1 &&
							rewardGridItems.length <= 3
						"
						class="reward-item-label-wrapper"
					>
						<ShrinkText
							:text="translate(`${item.frame_name}.name`)"
							:fontSize="12"
							:forcedFontSize="
								rewardGridItems.length > 2 ? 10 : 12
							"
							containerClassList="reward-item-label"
							isBold
							isUppercase
						/>
					</div>
				</template>
				<template #tooltip>
					<div class="reward-tooltip">
						<p
							v-html="
								item.reward_entity?.description.replace(
									/\{\/?b\}/g,
									'',
								)
							"
						/>
						<TileAggregationDetails
							v-if="item?.reward_entity"
							:entity="item.reward_entity"
							:isPromotion="true"
						/>
					</div>
				</template>
			</Tooltip>
		</div>
	</div>
</template>

<script lang="ts" setup>
import BidirectionalTextLabel from "@/components/BidirectionalTextLabel.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import RectangleBoxContainer from "@/components/SVG/RectangleBoxContainer.vue";
import TileAggregationDetails from "@/components/TileAggregationDetails.vue";
import Tooltip from "@/components/Tooltip.vue";
import type { FrameName } from "@/game/entities/config";
import type TileEntity from "@/game/entities/TileEntity";
import useTileRepository from "@/hooks/useTileRepository";
import type { BuildingSlotItem } from "@/types/shop";
import { OfferTemplate } from "@/types/shop";
import {
	LabelThemes,
	Placements,
	RewardsGridContext,
	TextAlign,
} from "@/types/ui";
import { switchParagraphFormat } from "@/utils/switchParagraphFormat";
import { getTileIconName } from "@/utils/tileIconName";
import { translate } from "@/utils/translate";
import { computed, markRaw } from "vue";

interface Props {
	items: BuildingSlotItem[] | null;
	context: RewardsGridContext;
	template: OfferTemplate;
}

const { items, context, template } = defineProps<Props>();

const tilerepo = markRaw(useTileRepository());

type RewardGridItem = BuildingSlotItem & {
	reward_entity?: TileEntity | null;
};

const rewardGridItems = computed((): RewardGridItem[] | null => {
	if (!items) return null;

	return items.map(
		({ group, frame_name, qty }: BuildingSlotItem): RewardGridItem => ({
			group,
			frame_name,
			qty,
			reward_entity: getBuildingEntity(frame_name as FrameName) ?? null,
		}),
	);
});

const count = computed((): number => rewardGridItems.value?.length ?? 0);

const hasTileAggregationDetails = computed((): boolean => {
	if (!rewardGridItems.value || rewardGridItems.value.length < 1)
		return false;
	if (
		context === RewardsGridContext.POPUP &&
		template === OfferTemplate.SLOTS_2 &&
		rewardGridItems.value.length === 1
	)
		return true;
	return false;
});

const dynamicSize = computed((): Record<string, number> => {
	// NOTE GD: Max 5 buildings
	if (context === RewardsGridContext.POPUP) {
		switch (count.value) {
			case 1:
				return { icon: 96, text: 60, gap: 16 };
			case 2:
				return { icon: 80, text: 48, gap: 10 };
			case 3:
				return { icon: 96, text: 48, gap: 10 };
			case 4:
				return { icon: 74, text: 36, gap: 6 };
			case 5:
				return { icon: 60, text: 30, gap: 6 };
			default:
				return { icon: 96, text: 48, gap: 10 };
		}
	} else {
		switch (count.value) {
			// TODO shop tile gfx
			default:
				return { icon: 80 };
		}
	}
});

const getBuildingEntity = (buildingFrameName: FrameName): TileEntity | null => {
	if (!buildingFrameName) return null;

	const entity = tilerepo.createEntityByFrameName(buildingFrameName);
	if (!entity) return null;

	return entity;
};
</script>

<style lang="scss" scoped>
.rewards-grid {
	display: grid;
	position: relative;
	grid-template-columns: repeat(3, 1fr);
	align-items: center;
	justify-items: center;
	z-index: 1;
	padding: 0.625rem;

	&::before {
		position: absolute;
		top: 50%;
		left: 50%;
		width: 140%;
		height: 140%;
		content: "";
		@include background(
			url($path-images + "shop/backgrounds/radial-glow-xl.avif"),
			contain,
			center,
			no-repeat
		);
		transform: translate(-50%, -50%);
		z-index: -1;
		animation: rotateInfinite 20s linear infinite;
		pointer-events: none;
	}

	&--single {
		&::before {
			top: 50%; // for radial-glow
		}

		.rewards-grid__value-item--horizontal {
			margin: 0 auto;
			max-width: 70%;

			.rewards-grid__value-aside {
				flex: 1 1 auto;
			}
		}
	}

	&--multiple {
		&::before {
			top: 0; // for radial-glow
		}
	}

	.price {
		:deep(.shrink-text) {
			color: #fff3dd;
		}
	}

	&__item {
		@include flex-col-center;
		row-gap: 6px;
		justify-items: center;
		width: 100%;

		.v-popper {
			width: 100%;
			@include flex-col-center;
		}
	}

	&__value {
		position: relative;
		width: 100%;

		&-item {
			&--horizontal {
				@include flex-row-center;
			}

			&--vertical {
				@include flex-col-center;
			}
		}

		&-aside {
			@include flex-col-center;
			align-items: flex-start;
			gap: 0.5rem;
		}
	}

	&__label {
		color: #f4d278;
		font-weight: 800;
		line-height: 1;
		text-align: left;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}

	&__content {
		position: relative;
		width: 100%;
		font-size: 0.75rem;
		text-align: center;
		@include flex-col-center;
		gap: 0;
		padding: 1.5rem 1rem 1rem;
		min-height: 3.813rem;

		&-wrapper {
			background: linear-gradient(
				0deg,
				rgba(56, 62, 45, 1) 0%,
				rgba(87, 93, 75, 1) 100%
			);

			width: 100%;
			@include flex-row-center;
			position: relative;
			margin-top: 2rem;
			box-shadow: inset 0 0 1rem 0rem #ffb330;
			background: linear-gradient(
				0deg,
				rgba(62, 45, 45, 1) 0%,
				rgba(90, 84, 84, 1) 100%
			);

			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 0.6;
				mix-blend-mode: color-burn;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 40%;
				width: 100%;
				height: 100%;
				content: "";
			}

			.rewards-label {
				position: absolute;
				top: -1rem;
				z-index: 1;
				width: auto;
				height: 2rem;
			}
		}

		:deep(.tile-aggregation-details-wrapper) {
			@include flex-col-center;

			.tile-aggregation-details {
				&[data-scope="population-consumption"] {
					order: 3;
				}

				&[data-scope="food-consumption"],
				&[data-scope="population-consumption"] {
					.tile-aggregation-details__label,
					.price__value {
						font-weight: 500 !important;
						font-size: 0.75rem !important;
					}
				}

				&[data-scope="fishing-boat-count"],
				&[data-scope="sea-exploration-range"],
				&[data-scope="population"],
				&[data-scope="exploration-speed"],
				&[data-scope="range"],
				&[data-scope="production"],
				&[data-scope="storage"] {
					.tile-aggregation-details__label,
					.tile-aggregation-details__value {
						height: 100%;
						font-weight: 800 !important;
						font-size: 1rem !important;

						.price__value {
							font-weight: 800 !important;
							font-size: 1rem !important;
						}
					}

					.tile-aggregation-details__value {
						align-items: baseline;
					}

					.tile-aggregation-details__value,
					.price__value {
						color: #138f36;
					}

					.tile-aggregation-details__value-unit,
					.tile-aggregation-details__value-comma {
						font-size: 0.75rem !important;
					}
				}
			}
		}

		:deep(strong) {
			color: #13cedf;
		}

		p,
		span,
		:deep(.tile-aggregation-details-wrapper) {
			color: #f2dcb5;

			.price__value {
				color: #f2dcb5 !important;
			}
		}
	}

	:deep(.reward-tooltip) {
		.price__value {
			text-shadow: none;
		}
	}

	/* 1 item: center */
	&--count-1 {
		grid-template-columns: 1fr;
		gap: 0.75rem;
		padding-bottom: 1rem;

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.5rem 2.5rem;
		}
	}

	/* 2 items: 2 columns */
	&--count-2 {
		grid-template-columns: repeat(2, 1fr);
		padding-bottom: 1rem;

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.375rem;
		}
	}

	/* 3 items: 1 top center, 2 bottom */
	&--count-3 {
		grid-template-columns: repeat(3, 1fr);
		grid-template-areas:
			". a ."
			"b . c";
		padding: 1rem 0.625rem 1rem;

		&.rewards-grid--card {
			column-gap: 0.25rem;
			row-gap: 0.25rem;
			padding: 0.375rem;
		}

		> .rewards-grid__item:nth-child(1) {
			grid-area: a;
			margin-bottom: -50%;
		}
		> .rewards-grid__item:nth-child(2) {
			grid-area: b;
			margin-top: -50%;
		}
		> .rewards-grid__item:nth-child(3) {
			grid-area: c;
			margin-top: -50%;
		}
	}

	/* 4 items: 2 top, 2 bottom */
	&--count-4 {
		grid-template-columns: repeat(4, 1fr);
		grid-template-areas:
			". a . b"
			"c . d .";
		column-gap: 0;
		row-gap: 0;
		padding: 1rem 0.625rem 1rem;

		&.rewards-grid--card {
			padding: 0.375rem;
		}

		> .rewards-grid__item:nth-child(1) {
			grid-area: a;
			margin-bottom: -50%;
		}
		> .rewards-grid__item:nth-child(2) {
			grid-area: b;
			margin-bottom: -50%;
		}
		> .rewards-grid__item:nth-child(3) {
			grid-area: c;
			margin-top: -50%;
		}
		> .rewards-grid__item:nth-child(4) {
			grid-area: d;
			margin-top: -50%;
		}
	}

	/* 5 items: 2 top, 3 bottom */
	&--count-5 {
		grid-template-columns: repeat(5, 1fr);
		grid-template-areas:
			". a . b ."
			"c . d . e";
		column-gap: 0;
		row-gap: 0;
		padding: 1rem 0.625rem 1rem;

		&.rewards-grid--card {
			padding: 0.375rem;
		}

		> .rewards-grid__item:nth-child(1) {
			grid-area: a;
			margin-bottom: -50%;
		}
		> .rewards-grid__item:nth-child(2) {
			grid-area: b;
			margin-bottom: -50%;
		}
		> .rewards-grid__item:nth-child(3) {
			grid-area: c;
			margin-top: -50%;
		}
		> .rewards-grid__item:nth-child(4) {
			grid-area: d;
			margin-top: -50%;
		}
		> .rewards-grid__item:nth-child(5) {
			grid-area: e;
			margin-top: -50%;
		}
	}

	.reward-item {
		&-aside-label {
			position: absolute;
			width: 100%;
			color: #f4d278;
			text-align: left;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

			// IMPORTANT: wrapper for ShrinkText container
			&-wrapper {
				position: relative;
				width: 100%;
				height: 2.25rem; // NOTE: predicted height of multi-word/line building label
				@include flex-row-center;
			}
		}

		&-label {
			position: absolute;
			margin-top: 0.5rem;
			width: 100%;
			color: #f2dcb5;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

			// IMPORTANT: wrapper for ShrinkText container
			&-wrapper {
				position: relative;
				width: 100%;
				height: 1.125rem;
				@include flex-row-center;
			}
		}
	}
}
</style>
